/**
 * UpgradeModal — Premium "soft-block" modal shown when:
 *  - User hits invoice quota (5 free / 20 starter)
 *  - User hits Copilot quota
 *  - Any 403 PLAN_LIMIT_REACHED / COPILOT_QUOTA_EXCEEDED from backend
 *
 * Usage: rendered globally in App.js via UpgradeModalProvider.
 */
import React from 'react';
import { Sparkles, X, ArrowRight, Check, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const PLAN_RECO = {
  free: {
    target: 'Starter',
    target_price: '14€',
    why: '20 factures/mois + simulation PPF/PDP',
    perks: [
      '20 factures par mois (au lieu de 5)',
      'Scanner IA 5 documents/mois',
      'Simulation PPF/PDP',
      'Factur-X conforme 2026',
    ],
  },
  starter: {
    target: 'Pro',
    target_price: '29€',
    why: 'Factures illimitées + Copilote IA illimité',
    perks: [
      'Factures illimitées',
      'Scanner IA illimité',
      'Copilote IA illimité',
      'Support prioritaire',
    ],
  },
};

export default function UpgradeModal({ open, reason, onClose }) {
  const navigate = useNavigate();
  if (!open) return null;

  const planId = reason?.plan_id || 'free';
  const reco = PLAN_RECO[planId] || PLAN_RECO.free;
  const isCopilotQuota = reason?.code === 'COPILOT_QUOTA_EXCEEDED';

  const handleUpgrade = () => {
    onClose?.();
    navigate('/pricing');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="upgrade-modal"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-slate-100 transition"
          data-testid="upgrade-modal-close"
          aria-label="Fermer"
        >
          <X className="h-5 w-5 text-slate-500" />
        </button>

        {/* Hero pistachio */}
        <div
          className="px-8 pt-10 pb-6 relative"
          style={{
            background:
              'linear-gradient(135deg, #DCF5C8 0%, #C9EFB4 50%, #B5E89F 100%)',
          }}
        >
          <div className="flex items-center gap-2 mb-3">
            <div className="h-9 w-9 rounded-xl bg-white/70 backdrop-blur flex items-center justify-center shadow-sm">
              <Sparkles className="h-5 w-5 text-emerald-700" />
            </div>
            <span className="text-xs font-semibold uppercase tracking-wider text-emerald-800/80">
              {isCopilotQuota ? 'Copilote IA' : 'Limite mensuelle atteinte'}
            </span>
          </div>
          <h2 className="text-2xl font-bold text-slate-900 leading-tight">
            {isCopilotQuota
              ? 'Vous adorez le Copilote IA — passons à la vitesse supérieure'
              : 'Vous cartonnez — il est temps de passer au plan supérieur'}
          </h2>
          <p className="mt-2 text-sm text-slate-700">
            {reason?.message ||
              `Vous avez atteint votre quota mensuel. Passez à ${reco.target} pour continuer sans interruption.`}
          </p>
        </div>

        {/* Body */}
        <div className="px-8 py-6">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                Plan recommandé
              </div>
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-3xl font-bold text-slate-900">{reco.target}</span>
                <span className="text-lg font-semibold text-emerald-600">
                  {reco.target_price}
                  <span className="text-sm text-slate-400">/mois</span>
                </span>
              </div>
              <div className="text-sm text-slate-600 mt-1">{reco.why}</div>
            </div>
            <div className="hidden sm:flex items-center justify-center h-12 w-12 rounded-2xl bg-emerald-50">
              <Zap className="h-6 w-6 text-emerald-600" fill="currentColor" />
            </div>
          </div>

          <ul className="space-y-2.5 mt-5">
            {reco.perks.map((perk) => (
              <li key={perk} className="flex items-start gap-2.5">
                <div className="mt-0.5 flex-shrink-0 h-5 w-5 rounded-full bg-emerald-100 flex items-center justify-center">
                  <Check className="h-3 w-3 text-emerald-700" strokeWidth={3} />
                </div>
                <span className="text-sm text-slate-700">{perk}</span>
              </li>
            ))}
          </ul>

          <button
            onClick={handleUpgrade}
            className="mt-7 w-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold py-3.5 rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/30 hover:shadow-emerald-500/40 hover:-translate-y-0.5"
            data-testid="upgrade-modal-cta"
          >
            Passer à {reco.target}
            <ArrowRight className="h-4 w-4" />
          </button>

          <button
            onClick={onClose}
            className="mt-2 w-full text-sm text-slate-500 hover:text-slate-700 py-2 transition"
            data-testid="upgrade-modal-later"
          >
            Plus tard
          </button>

          <p className="mt-4 text-xs text-center text-slate-400">
            Annulation à tout moment · Sans engagement · Conformité 2026 incluse
          </p>
        </div>
      </div>
    </div>
  );
}
